const INVALID_NANP_LEADING_DIGIT = /^[01]/;
export const PHONE_EXTENSION_SUFFIX =
  /\s*(?<![a-z])(?:ext\.?|extension|x|#)\s*(\d{1,6})\s*$/i;

function isValidNanpNumber(tenDigits: string): boolean {
  const areaCode = tenDigits.slice(0, 3);
  const exchange = tenDigits.slice(3, 6);
  if (INVALID_NANP_LEADING_DIGIT.test(areaCode))
    return false;
  if (INVALID_NANP_LEADING_DIGIT.test(exchange))
    return false;
  return true;
}

export const PHONE_FORMAT = {
  canonical: "5551234567",
  display: "(555) 123-4567",
  pattern: /^\d{10}$/,

  validate(value: string): boolean {
    const normalized = this.normalize(value);
    return normalized !== undefined;
  },

  diagnose(value: string): string | undefined {
    if (!value || value.trim() === "") return undefined;
    const digits = value.replace(/\D/g, "");

    let tenDigits: string | undefined;
    if (digits.length === 10) {
      tenDigits = digits;
    } else if (digits.length === 11 && digits.startsWith("1")) {
      tenDigits = digits.slice(1);
    }

    if (!tenDigits) {
      if (digits.length < 10) {
        return `Phone number too short — need 10 digits, got ${digits.length}`;
      }
      return `Phone number too long — need 10 digits, got ${digits.length}`;
    }

    const areaCode = tenDigits.slice(0, 3);
    const exchange = tenDigits.slice(3, 6);
    if (INVALID_NANP_LEADING_DIGIT.test(areaCode)) {
      return `Invalid area code (${areaCode}) — cannot start with 0 or 1`;
    }
    if (INVALID_NANP_LEADING_DIGIT.test(exchange)) {
      return `Invalid exchange (${exchange}) — cannot start with 0 or 1`;
    }

    return undefined;
  },

  normalize(value: string | undefined | null): string | undefined {
    if (!value || value.trim() === "") return undefined;
    const digits = value.replace(/\D/g, "");

    let tenDigits: string | undefined;
    if (digits.length === 10) {
      tenDigits = digits;
    } else if (digits.length === 11 && digits.startsWith("1")) {
      tenDigits = digits.slice(1);
    }

    if (tenDigits && isValidNanpNumber(tenDigits)) {
      return tenDigits;
    }

    return undefined;
  },

  format(value: string): string {
    let digits = value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  },

  toE164(value: string | undefined | null): string | undefined {
    const normalized = this.normalize(value);
    return normalized ? `+1${normalized}` : undefined;
  },

  toE164WithExtension(value: string | undefined | null): string | undefined {
    if (!value) return undefined;
    const extensionMatch = PHONE_EXTENSION_SUFFIX.exec(value);
    if (!extensionMatch) return this.toE164(value);
    const e164 = this.toE164(value.slice(0, extensionMatch.index));
    return e164 ? `${e164} x${extensionMatch[1]}` : undefined;
  },

  errorMessage: "Enter a valid US phone number (e.g., 555-234-5678)",
} as const;
